import { create } from "zustand";
import { subscribeWithSelector, persist } from "zustand/middleware";
import { defaultLocale } from "@src/utils/i18n";
import {
  getHighlightOnHoverFunction,
  getUnhighlightOnHoverFunction,
} from "@src/events/mapEvents";
import {
  shallowCompareArray,
  addAndRemoveLayer,
  getHoverLayer,
  sortLayersByOrder,
} from "@utils/helpers";
import { recordEvent } from "@api/survey";
import { TiledMapService } from "mapbox-gl-esri-sources";

export const schoolGroupColors = {
  Enhancement: "#7F2367",
  Optimization: "#213E96",
  Expansion: "#10867E",
  Continuation: "#A7A9AC",
  Other: "#333333",
  "N/A": "#65636D",
};

export const usePersistentSurveyStore = create(
  persist(
    (set, get) => ({
      language: defaultLocale,
      getLanguage: () => get().language,
      setLanguage: (locale) => set({ language: locale }),
      userUUID: null,
      getUserUUID: () => get().userUUID,
      setUserUUID: (userUUID) => set({ userUUID }),
    }),
    {
      name: "survey-store",
    },
  ),
);

export const useSurveyStore = create(
  subscribeWithSelector((set) => ({
    getMapRef: () => null,
    setMapRef: (mapRef) => {
      set({
        getMapRef: () => mapRef.current,
      });
    },
    mapIsLoaded: false,
    setMapIsLoaded: (mapIsLoaded) => set({ mapIsLoaded }),
    schools: [],
    setSchools: (schools) => set({ schools }),
    sessionUUID: self.crypto.randomUUID(),
    setSessionUUID: (sessionUUID) => set({ sessionUUID }),
    surveySections: [],
    setSurveySections: (surveySections) => set({ surveySections }),
    surveyResponses: {},
    setSurveyResponses: (fn) =>
      set((responses) => ({ surveyResponses: fn(responses) })),
    commentQuestion: null,
    setCommentQuestion: (commentQuestion) => set({ commentQuestion }),
    selectedSchool: null,
    setSelectedSchool: (school) => set({ selectedSchool: school }),
    currentSurveySection: null,
    setCurrentSurveySection: (surveySection) =>
      set({ currentSurveySection: surveySection }),
    surveySectionLayers: [],
    setSurveySectionLayers: (fn) =>
      set((layers) => ({ surveySectionLayers: fn(layers) })),
    hoverFeature: null,
    setHoverFeature: (feature) => set({ hoverFeature: feature }),
    mapComponentRefs: [],
    setMapComponentRefs: (mapComponentRefs) => set({ mapComponentRefs }),
    appendMapComponentRef: (mapComponentRef) =>
      set((state) => ({
        mapComponentRefs: [...state.mapComponentRefs, mapComponentRef],
      })),
    mapMarkers: [],
    setMapMarkers: (mapMarkers) => set({ mapMarkers }),
    appendMapMarker: (mapMarker) =>
      set((state) => ({ mapMarkers: [...state.mapMarkers, mapMarker] })),
    satellite: false,
    setSatellite: (satellite) => set({ satellite }),
  })),
);

export const onMapLoadComplete = useSurveyStore.subscribe(
  (state) => [state.getMapRef, state.mapIsLoaded],
  ([getMapRef, mapIsLoaded]) => {
    const mapRef = getMapRef();
    const satellite = useSurveyStore.getState().satellite;

    if (mapRef && mapIsLoaded) {
      const sourceKey = "satellite";

      if (!mapRef.getSource(sourceKey)) {
        try {
          new TiledMapService(sourceKey, mapRef, {
            url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer",
            attribution: "Tiles © Esri — Source: Esri",
          });
        } catch (e) {
          return;
        }
      }

      if (mapRef.getSource(sourceKey) && !mapRef.getLayer("satellite")) {
        mapRef.addLayer(
          {
            id: "satellite",
            type: "raster",
            source: sourceKey,
            layout: {
              visibility: satellite ? "visible" : "none",
            },
          },
          "board_districts-line",
        );
      }
    }
  },
);

export const showHideSatellite = useSurveyStore.subscribe(
  (state) => [state.getMapRef, state.mapIsLoaded, state.satellite],
  ([getMapRef, mapIsLoaded, satellite]) => {
    const mapRef = getMapRef();
    if (mapRef && mapIsLoaded) {
      try {
        if (satellite) {
          mapRef.setLayoutProperty("satellite", "visibility", "visible");
        } else {
          mapRef.setLayoutProperty("satellite", "visibility", "none");
        }
      } catch (e) {
        // typically because the layer isn't loaded yet
        console.error(e);
        return;
      }
    }
  },
);

// if mapRef is lost, then remove all the map markers and unmount the map components
export const onMapRefChange = useSurveyStore.subscribe(
  (state) => [state.mapComponentRefs, state.getMapRef],
  ([mapComponentRefs, getMapRef]) => {
    const mapRef = getMapRef();
    if (!mapRef) {
      mapComponentRefs.forEach((mapComponentRef) => {
        mapComponentRef.unmount();
        mapComponentRef.current = null;
      });
      useSurveyStore.setState({ mapMarkers: [] });
    }
  },
  { equalityFn: shallowCompareArray },
);

export const onSurveySectionLayersChange = useSurveyStore.subscribe(
  (state) => [state.surveySectionLayers, state.getMapRef, state.mapIsLoaded],
  ([surveySectionLayers, getMapRef, mapIsLoaded]) => {
    const mapRef = getMapRef();
    if (mapRef && mapIsLoaded && surveySectionLayers.length > 0) {
      const sortedLayers = sortLayersByOrder(surveySectionLayers);
      sortedLayers.forEach((layer) => {
        try {
          addAndRemoveLayer(mapRef, layer);
          const highlightFunction = getHighlightOnHoverFunction(layer.layer.id);
          mapRef.on(highlightFunction.action, highlightFunction.layer, (e) => {
            highlightFunction.handler(e, mapRef);
          });
          const unhighlightFunction = getUnhighlightOnHoverFunction(
            layer.layer.id,
          );
          mapRef.on(
            unhighlightFunction.action,
            unhighlightFunction.layer,
            (e) => {
              unhighlightFunction.handler(e, mapRef);
            },
          );
          if (["fill", "circle"].includes(layer.layer.type)) {
            const hoverLayer = getHoverLayer(layer);
            addAndRemoveLayer(mapRef, hoverLayer);
          }
        } catch (e) {
          console.error(e);
          return;
        }
      });
    }
  },
  { equalityFn: shallowCompareArray },
);

useSurveyStore.subscribe(
  (state) => state.sessionUUID,
  (sessionUUID) => {
    if (!sessionUUID) {
      useSurveyStore.setState({ sessionUUID: self.crypto.randomUUID() });
    }
  },
);

useSurveyStore.subscribe(
  (state) => state.commentQuestion,
  (commentQuestion) => {
    const mapRef = useSurveyStore.getState().getMapRef();
    if (mapRef && commentQuestion) {
      mapRef.getCanvas().style.cursor = "pointer";
    } else if (mapRef) {
      mapRef.getCanvas().style.cursor = "";
    }
  },
);

useSurveyStore.subscribe(
  (state) => state.currentSurveySection,
  (currentSurveySection) => {
    if (!currentSurveySection) {
      useSurveyStore.getState().mapComponentRefs.forEach((ref) => {
        ref.unmount();
      });
      useSurveyStore.getState().mapMarkers.forEach((marker) => {
        marker.remove();
      });
      useSurveyStore.setState({
        sessionUUID: null,
        surveyResponses: {},
        commentQuestion: null,
        mapComponentRefs: [],
        mapMarkers: [],
      });
    }
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  },
);

useSurveyStore.subscribe(
  (state) => state.hoverFeature,
  (hoverFeature) => {
    const mapRef = useSurveyStore.getState().getMapRef();
    if (!(hoverFeature?.id + 1) || !mapRef) return;
    mapRef.setFeatureState(
      { source: hoverFeature.source, id: hoverFeature.id },
      { hover: true },
    );
  },
  {
    equalityFn: (hoverFeature, prevHoverFeature) => {
      return (
        hoverFeature?.id === prevHoverFeature?.id &&
        hoverFeature?.source === prevHoverFeature?.source
      );
    },
  },
);

// Events

useSurveyStore.subscribe(
  (state) => state.selectedSchool,
  (selectedSchool) => {
    const surveyData = useSurveyStore.getState();
    recordEvent({
      eventType: "selected_school",
      sessionUUID: surveyData.sessionUUID,
      eventData: {
        selectedSchool: selectedSchool?.properties,
        source: selectedSchool?.source,
      },
      surveySectionSlug: surveyData.currentSurveySection,
    });
  },
  {
    equalityFn: (selectedSchool, prevSelectedSchool) => {
      return (
        selectedSchool?.properties.school_id ===
        prevSelectedSchool?.properties.school_id
      );
    },
  },
);
